/**
 * Phase 5.2 (2026-05-15) — Hub tickets / factures / devis.
 *
 * Single landing page that aggregates the 3 most-used streams of the
 * shop: open repair tickets, recent POS sales (factures), and devis
 * (quotes). Includes a unified search bar (REM/POS/DEV prefix detection),
 * 4 quick-filter chips, and per-card quick actions (view / print / mail /
 * mark-ready).
 *
 * Routes: `/hub` (admin/staff only).
 * Reuses existing pages via deep-links — no breaking changes elsewhere.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import {
  Search, RefreshCw, Eye, Printer, Mail, CheckCircle2, Clock,
  Wrench, Receipt, FileText, AlertTriangle, Loader2, Store,
} from 'lucide-react';

// Date formatting — locale-aware via the i18n formatDate helper.
// Preserves the original parsing logic (space→T, append Z) and fallbacks.
const fmtDate = (s, formatDate) => {
  if (!s) return '—';
  try {
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
    if (Number.isNaN(d.getTime())) return s.toString().slice(0, 10);
    return formatDate(d, { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return s.toString().slice(0, 10); }
};

const STATUS_COLORS = {
  'En attente': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'En cours':   'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Pret':       'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Prêt':       'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Livré':      'bg-gray-500/15 text-gray-300 border-gray-500/30',
  'Livre':      'bg-gray-500/15 text-gray-300 border-gray-500/30',
  'Payée':      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'facture':    'bg-gray-500/15 text-gray-400 border-gray-500/30',
  'envoye':     'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'envoyé':     'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'accepte':    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

// Mapping statut backend (FR) -> clé i18n pour l'AFFICHAGE uniquement.
// La logique (couleurs, filtres, règles métier) reste indexée sur la valeur brute.
// Repli sur la valeur brute si le statut n'est pas connu.
const STATUS_KEYS = {
  'en attente': 'hub.statusWaiting',
  'en cours':   'hub.statusInProgress',
  'pret':       'hub.statusReady',
  'prêt':       'hub.statusReady',
  'livré':      'hub.statusDelivered',
  'livre':      'hub.statusDelivered',
  'payée':      'hub.statusPaid',
  'payee':      'hub.statusPaid',
  'facture':    'hub.statusInvoiced',
  'facturé':    'hub.statusInvoiced',
  'facturée':   'hub.statusInvoiced',
  'envoye':     'hub.statusSent',
  'envoyé':     'hub.statusSent',
  'accepte':    'hub.statusAccepted',
  'accepté':    'hub.statusAccepted',
  'annule':     'hub.statusCancelled',
  'annulé':     'hub.statusCancelled',
  'termine':    'hub.statusDone',
  'terminé':    'hub.statusDone',
};

// STEP 3.5 multi-magasin (2026-05-15) — mini badge showing the current
// POS magasin (read-only on the Hub for now; full filter coming in STEP 4).
const HubMagasinBadge = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = React.useState('M1');
  const [name, setName] = React.useState(t('hub.magasinDefaultName'));
  React.useEffect(() => {
    let alive = true;
    try {
      const code = (typeof window !== 'undefined' && window.localStorage.getItem('pos_current_magasin_id')) || 'M1';
      if (alive) setCurrent(code);
    } catch (_) { /* noop */ }
    // resolve readable name (best-effort)
    (async () => {
      try {
        const { default: axios } = await import('axios');
        const token = localStorage.getItem('access_token');
        const code = window.localStorage.getItem('pos_current_magasin_id') || 'M1';
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/magasins?active_only=true`;
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const m = (r.data?.magasins || []).find(x => x.code === code);
        if (alive && m) setName(m.name);
      } catch (_) { /* fallback to default name */ }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      data-testid="hub-magasin-badge"
      title={t('hub.magasinBadgeTitle', { name })}
    >
      <Store className="w-3 h-3" />
      {current} — {name}
    </span>
  );
};

const HubPage = () => {  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { t, formatCurrency, formatNumber } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ tickets: [], factures: [], devis: [], kpis: {} });
  const [filter, setFilter] = useState('all'); // all | today | overdue | paid | unpaid
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // PhaseC (2026-02) — Liste des magasins actifs pour le double counter sticky.
  const [magasinsList, setMagasinsList] = useState([]);
  const [counterCompact, setCounterCompact] = useState(false);
  const searchTimeout = useRef(null);

  const loadSummary = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const res = await api.get('/hub/summary');
      setData(res.data);
    } catch (err) {
      console.error('hub summary load failed', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // PhaseC — Charge la liste des magasins (1 call, no refresh besoin)
  useEffect(() => {
    api.get('/magasins').then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.magasins || []);
      const list = raw.filter(m => m.is_active !== false);
      setMagasinsList(list);
    }).catch(() => setMagasinsList([]));
  }, [api]);

  // Auto-refresh every 60 s (silent — no skeleton flicker).
  useEffect(() => {
    const timer = setInterval(() => loadSummary(true), 60000);
    return () => clearInterval(timer);
  }, [loadSummary]);

  // Debounced search
  useEffect(() => {
    if (!searchQ.trim()) {
      setSearchRes(null);
      setSearchLoading(false);
      return;
    }
    clearTimeout(searchTimeout.current);
    setSearchLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get(`/hub/search?q=${encodeURIComponent(searchQ.trim())}`);
        setSearchRes(res.data);
      } catch (err) {
        console.error('search failed', err);
        setSearchRes({ tickets: [], factures: [], devis: [], counts: { tickets: 0, factures: 0, devis: 0 } });
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQ, api]);

  // Filter applies to all 3 columns when not searching.
  const filteredData = useMemo(() => {
    if (searchRes) return searchRes;
    if (filter === 'all') return data;
    const today = new Date().toISOString().slice(0, 10);

    const passTicket = (t) => {
      const s = (t.status || '').toLowerCase();
      if (filter === 'today') return (t.created_at || '').startsWith(today);
      if (filter === 'overdue') return t.promised_at && t.promised_at < today && !['livré','livre','termine','terminé'].includes(s);
      if (filter === 'paid') return !t.reste_a_payer || t.reste_a_payer <= 0;
      if (filter === 'unpaid') return t.reste_a_payer && t.reste_a_payer > 0;
      return true;
    };
    const passFacture = (f) => {
      if (filter === 'today') return (f.created_at || '').startsWith(today);
      if (filter === 'paid') return true;  // POS = paid
      if (filter === 'unpaid') return false;
      if (filter === 'overdue') return false;
      return true;
    };
    const passDevis = (d) => {
      const s = (d.status || '').toLowerCase();
      if (filter === 'today') return (d.created_at || '').startsWith(today);
      if (filter === 'paid') return !!d.facture_ref;
      if (filter === 'unpaid') return !d.facture_ref && s !== 'annule' && s !== 'annulé';
      if (filter === 'overdue') return false;
      return true;
    };
    return {
      tickets: data.tickets.filter(passTicket),
      factures: data.factures.filter(passFacture),
      devis: data.devis.filter(passDevis),
    };
  }, [data, filter, searchRes]);

  const filters = [
    { id: 'all',     label: t('hub.filterAll')     },
    { id: 'today',   label: t('hub.filterToday')   },
    { id: 'overdue', label: t('hub.filterOverdue') },
    { id: 'paid',    label: t('hub.filterPaid')    },
    { id: 'unpaid',  label: t('hub.filterUnpaid')  },
  ];

  const role = (user?.role || '').toLowerCase();
  const canPrint = ['admin','super_admin','staff','technicien','employe'].includes(role);

  const queueTicket80 = useCallback(async (spec) => {
    try {
      await printTicket80mm(api, spec);
    } catch (err) {
      console.error('hub ticket print failed', err);
      alert(err.response?.data?.detail || t('hub.errorPrint'));
    }
  }, [api, t]);

  // PDF A4 uniquement (devis hub) — pas ticket 80mm.
  const openPdfForPrint = useCallback(async (endpoint) => {
    try {
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert(t('hub.errorPopupBlocked'));
        return;
      }
      const triggerPrint = () => { printWindow.focus(); printWindow.print(); };
      printWindow.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (err) {
      console.error('hub print failed', err);
      alert(err.response?.data?.detail || t('hub.errorPrint'));
    }
  }, [api, t]);

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6" data-testid="hub-page">
        {/* Header sticky */}
        <div className="sticky top-0 z-30 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-gradient-to-b from-gray-950 to-gray-950/95 backdrop-blur-md border-b border-white/5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
                {t('hub.title')}
                {/* STEP 3.5 multi-magasin — mini badge magasin (read-only display) */}
                <HubMagasinBadge />
              </h1>
              <div className="text-xs text-gray-500 mt-0.5">
                {data.kpis ? (
                  <>
                    <span data-testid="hub-kpi-tickets-open">{t('hub.kpiTicketsOpen', { n: data.kpis.tickets_open || 0 })}</span>
                    <span className="mx-2 text-gray-700">•</span>
                    <span data-testid="hub-kpi-factures-today">{t('hub.kpiFacturesToday', { n: data.kpis.factures_today_count || 0, total: formatCurrency(Number(data.kpis.factures_today_total || 0)) })}</span>
                    <span className="mx-2 text-gray-700">•</span>
                    <span data-testid="hub-kpi-devis-open">{t('hub.kpiDevisOpen', { n: data.kpis.devis_open || 0 })}</span>
                  </>
                ) : '…'}
              </div>
            </div>
            <button
              onClick={() => loadSummary()}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-sm font-medium disabled:opacity-50"
              data-testid="hub-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('hub.refresh')}
            </button>
          </div>

          {/* PhaseC (2026-02) — Double counter sticky par magasin (dynamique).
              Calculé client-side à partir de data.tickets / data.devis / data.factures.
              Itère sur magasinsList — jamais M1/M2 en dur. */}
          {magasinsList.length > 0 && (() => {
            const today = new Date().toISOString().slice(0, 10);
            const statsByMag = {};
            (data.tickets || []).forEach(t => {
              const m = t.magasin_id || '—';
              statsByMag[m] = statsByMag[m] || { tickets: 0, devis: 0, ca_today: 0 };
              statsByMag[m].tickets += 1;
            });
            (data.devis || []).forEach(d => {
              const m = d.magasin_id || '—';
              statsByMag[m] = statsByMag[m] || { tickets: 0, devis: 0, ca_today: 0 };
              statsByMag[m].devis += 1;
            });
            (data.factures || []).forEach(f => {
              if ((f.created_at || '').startsWith(today)) {
                const m = f.magasin_id || '—';
                statsByMag[m] = statsByMag[m] || { tickets: 0, devis: 0, ca_today: 0 };
                statsByMag[m].ca_today += Number(f.total || 0);
              }
            });
            return (
              <div
                className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
                data-testid="hub-double-counter"
              >
                <button
                  onClick={() => setCounterCompact(v => !v)}
                  className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 px-1.5 py-0.5 rounded border border-white/5 hover:border-white/15 flex-shrink-0"
                  title={counterCompact ? t('hub.counterViewDetailed') : t('hub.counterViewCompact')}
                  data-testid="hub-counter-toggle"
                >{counterCompact ? '+' : '−'}</button>
                {magasinsList.map(m => {
                  const s = statsByMag[m.code] || { tickets: 0, devis: 0, ca_today: 0 };
                  return (
                    <div
                      key={m.code}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border bg-white/[0.02] flex-shrink-0"
                      style={{ borderColor: `${m.color || '#10b981'}55` }}
                      data-testid={`hub-counter-${m.code}`}
                      title={t('hub.counterMagasinTitle', { name: m.name, tickets: s.tickets, devis: s.devis, total: formatCurrency(s.ca_today) })}
                    >
                      <span
                        className="text-[11px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: `${m.color || '#10b981'}33`, color: m.color || '#10b981' }}
                      >{m.code}</span>
                      {counterCompact ? (
                        <span className="text-xs text-gray-300 font-mono">
                          {t('hub.counterCompact', { tickets: s.tickets, devis: s.devis, total: formatCurrency(s.ca_today, 'EUR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) })}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300 flex items-center gap-1.5">
                          <span><span className="text-amber-400 font-semibold">{s.tickets}</span> {t('hub.counterTickets')}</span>
                          <span className="text-gray-700">·</span>
                          <span><span className="text-sky-400 font-semibold">{s.devis}</span> {t('hub.counterDevis')}</span>
                          <span className="text-gray-700">·</span>
                          <span><span className="text-emerald-400 font-semibold">{formatNumber(s.ca_today, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> {t('hub.counterPerDay')}</span>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Unified search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={t('hub.searchPlaceholder')}
              className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500/40 focus:bg-white/10 focus:outline-none text-sm transition-all"
              data-testid="hub-search-input"
            />
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 animate-spin" />
            )}
            {searchQ && !searchLoading && (
              <button
                onClick={() => setSearchQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white"
                data-testid="hub-search-clear"
              >
                ✕
              </button>
            )}
          </div>

          {/* Filter chips */}
          {!searchRes && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {filters.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    filter === f.id
                      ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`hub-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <HubColumn
            title={t('hub.columnReparations')}
            icon={<Wrench className="w-5 h-5" />}
            color="amber"
            items={filteredData.tickets || []}
            loading={loading}
            seeAllUrl="/reparations"
            renderItem={(ticket) => <TicketCard ticket={ticket} navigate={navigate} canPrint={canPrint} onPrintTicket={queueTicket80} />}
            testIdPrefix="hub-tickets"
          />
          <HubColumn
            title={t('hub.columnFactures')}
            icon={<Receipt className="w-5 h-5" />}
            color="emerald"
            items={filteredData.factures || []}
            loading={loading}
            seeAllUrl="/activite?tab=ventes"
            renderItem={(f) => <FactureCard f={f} navigate={navigate} canPrint={canPrint} onPrintTicket={queueTicket80} />}
            testIdPrefix="hub-factures"
          />
          <HubColumn
            title={t('hub.columnDevis')}
            icon={<FileText className="w-5 h-5" />}
            color="sky"
            items={filteredData.devis || []}
            loading={loading}
            seeAllUrl="/devis-deplacement"
            renderItem={(d) => <DevisCard d={d} navigate={navigate} canPrint={canPrint} onPrint={openPdfForPrint} />}
            testIdPrefix="hub-devis"
          />
        </div>
      </div>
    </Layout>
  );
};

// ────────────────────────── Column ──────────────────────────
const HubColumn = ({ title, icon, color, items, loading, seeAllUrl, renderItem, testIdPrefix }) => {
  const { t } = useLanguage();
  const colorMap = {
    amber: 'border-amber-500/30 bg-amber-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    sky: 'border-sky-500/30 bg-sky-500/5',
  };
  const navigate = useNavigate();
  return (
    <div className={`rounded-2xl border ${colorMap[color] || ''} p-4`} data-testid={`${testIdPrefix}-column`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gray-300">
          {icon}
          {title}
          <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-white/10 text-gray-400">{items.length}</span>
        </div>
        <button
          onClick={() => navigate(seeAllUrl)}
          className="text-xs text-gray-400 hover:text-white"
          data-testid={`${testIdPrefix}-see-all`}
        >
          {t('hub.seeAll')} →
        </button>
      </div>
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-white/5 animate-pulse" />
          ))
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-8" data-testid={`${testIdPrefix}-empty`}>
            <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
            {t('hub.empty')}
          </div>
        ) : (
          items.map((it, i) => (
            <div key={it.id || it.ref || i} data-testid={`${testIdPrefix}-item-${i}`}>
              {renderItem(it)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ────────────────────────── Cards ──────────────────────────
const StatusPill = ({ status }) => {
  const { t } = useLanguage();
  if (!status) return null;
  const cls = STATUS_COLORS[status] || 'bg-white/10 text-gray-300 border-white/10';
  const key = STATUS_KEYS[String(status).toLowerCase()];
  const label = key ? t(key) : status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {label}
    </span>
  );
};

const QuickActions = ({ onView, onPrint, onMail, onMarkReady, canPrint, showMarkReady }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-1 mt-2 -mb-1">
      {onView && (
        <button onClick={onView} className="p-1.5 rounded-lg bg-white/5 hover:bg-sky-500/20 hover:text-sky-300 transition-all" title={t('hub.actionView')}>
          <Eye className="w-3.5 h-3.5" />
        </button>
      )}
      {canPrint && onPrint && (
        <button onClick={onPrint} className="p-1.5 rounded-lg bg-white/5 hover:bg-amber-500/20 hover:text-amber-300 transition-all" title={t('hub.actionPrint')}>
          <Printer className="w-3.5 h-3.5" />
        </button>
      )}
      {onMail && (
        <button onClick={onMail} className="p-1.5 rounded-lg bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-300 transition-all" title={t('hub.actionMail')}>
          <Mail className="w-3.5 h-3.5" />
        </button>
      )}
      {showMarkReady && onMarkReady && (
        <button onClick={onMarkReady} className="p-1.5 rounded-lg bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-300 transition-all" title={t('hub.actionMarkReady')}>
          <CheckCircle2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

const TicketCard = ({ ticket, navigate, canPrint, onPrintTicket }) => {
  const { t, formatDate, formatCurrency } = useLanguage();
  const status = ticket.status || '';
  const isOverdue = ticket.promised_at && ticket.promised_at < new Date().toISOString().slice(0, 10) && !['Livré','Livre'].includes(status);
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3 hover:bg-white/10 transition-all" data-testid={`hub-ticket-${ticket.ref}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <button
          onClick={() => navigate(ticket.url)}
          className="font-mono text-sm font-semibold text-amber-300 hover:underline"
        >
          {ticket.ref}
        </button>
        <StatusPill status={status} />
      </div>
      <div className="text-sm text-gray-200 truncate">{ticket.client_name || '—'}</div>
      {ticket.model && <div className="text-xs text-gray-500 truncate">{ticket.model}</div>}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
        <span>{fmtDate(ticket.created_at, formatDate)}</span>
        <span className="text-gray-300">{formatCurrency(Number(ticket.price || 0))}</span>
      </div>
      {isOverdue && (
        <div className="flex items-center gap-1 text-xs text-red-300 mt-1">
          <AlertTriangle className="w-3 h-3" /> {t('hub.filterOverdue')}
        </div>
      )}
      <QuickActions
        canPrint={canPrint}
        onView={() => navigate(ticket.url)}
        onPrint={ticket.ref ? () => onPrintTicket({ type: 'repair', ref: ticket.ref }) : null}
        showMarkReady={false}
      />
    </div>
  );
};

const FactureCard = ({ f, navigate, canPrint, onPrintTicket }) => {
  const { t, formatDate, formatCurrency } = useLanguage();
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3 hover:bg-white/10 transition-all" data-testid={`hub-facture-${f.ref}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <button
          onClick={() => navigate(f.url)}
          className="font-mono text-xs font-semibold text-emerald-300 hover:underline truncate"
        >
          {(f.ref || '').replace(/^POS-/, '')}
        </button>
        <StatusPill status={f.status || 'Payée'} />
      </div>
      <div className="text-sm text-gray-200 truncate">{f.client_name || t('hub.walkinClient')}</div>
      {f.designation && <div className="text-xs text-gray-500 truncate">{f.designation}</div>}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
        <span>{fmtDate(f.created_at, formatDate)}</span>
        <span className="text-emerald-300 font-semibold">{formatCurrency(Number(f.total || 0))}</span>
      </div>
      <QuickActions
        canPrint={canPrint}
        onView={() => navigate(f.url)}
        onPrint={f.ref ? () => onPrintTicket({ type: 'vente', ref: f.ref }) : null}
      />
    </div>
  );
};

const DevisCard = ({ d, navigate, canPrint, onPrint }) => {
  const { t, formatDate, formatCurrency } = useLanguage();
  const converted = !!d.facture_ref;
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3 hover:bg-white/10 transition-all" data-testid={`hub-devis-${d.ref}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <button
          onClick={() => navigate(d.url)}
          className="font-mono text-sm font-semibold text-sky-300 hover:underline"
        >
          {d.ref}
        </button>
        <div className="flex flex-col items-end gap-1">
          <StatusPill status={d.status} />
          {d.is_b2b && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">B2B</span>
          )}
        </div>
      </div>
      <div className="text-sm text-gray-200 truncate">{d.client_name || '—'}</div>
      {converted && (
        <div className="text-xs text-gray-400 truncate">{t('hub.invoicedAs', { ref: d.facture_ref })}</div>
      )}
      <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
        <span>{fmtDate(d.created_at, formatDate)}</span>
        <span className="text-sky-300 font-semibold">{formatCurrency(Number(d.total || 0))}</span>
      </div>
      <QuickActions
        canPrint={canPrint}
        onView={() => navigate(d.url)}
        onPrint={d.id ? () => onPrint(`/devis/${encodeURIComponent(d.id)}/pdf`) : null}
      />
    </div>
  );
};

export default HubPage;
